import { colors, Theme } from '@mui/material'
import {
  AvcInstanceHealthEnum,
  AvcInstanceTask,
  AvcInstanceTaskHealthEnum,
  AvcInstanceTaskStateEnum,
  AvcTaskHealthEnum,
  AvcTaskStateEnum,
} from '@nativewaves/nw-cloud-sdk/content'

import { blackShades, whiteShades } from '@shared/components/ThemeProvider'
import { ThemeTypes } from 'stores/settings'
import { AlertSeverities, EntityStates, HealthStates, LogEntryLevels, ResourceStates } from 'types/api'

const variant = (mode?: Theme['palette']['mode']) => (mode === ThemeTypes.DARK ? 700 : 300)

export function getAvcInstanceStateColor(
  state: AvcInstanceTask['state'],
  health: AvcInstanceTask['health'] = AvcInstanceTaskHealthEnum.Ok,
  heartbeatExpired: AvcInstanceTask['heartbeatExpired'] = false,
  returnTransparent?: boolean,
) {
  if (heartbeatExpired) {
    return colors.red
  }
  if (state === AvcInstanceTaskStateEnum.Progressing) {
    switch (health) {
      case AvcInstanceTaskHealthEnum.Ok:
        return colors.green
      case AvcInstanceTaskHealthEnum.Warning:
        return colors.amber
      case AvcInstanceTaskHealthEnum.Critical:
        return colors.red
    }
  }
  switch (state) {
    case AvcInstanceTaskStateEnum.Stopped:
      return returnTransparent ? 'transparent' : colors.grey
    // TODO: remove quick fix when obsolete
    case AvcInstanceTaskStateEnum.Stopping:
      return colors.deepOrange
    case AvcInstanceTaskStateEnum.Pending:
    case AvcInstanceTaskStateEnum.Starting:
    case AvcInstanceTaskStateEnum.Provisioning:
    case AvcInstanceTaskStateEnum.Rescheduled:
      return colors.blue
    case AvcInstanceTaskStateEnum.Completed:
      return colors.green
    case AvcInstanceTaskStateEnum.Retrying:
      return colors.amber
    case AvcInstanceTaskStateEnum.Blocked:
    case AvcInstanceTaskStateEnum.Failed:
      return colors.red
    default:
      return returnTransparent ? 'transparent' : colors.grey
  }
}

export const getAvcInstanceHealthColor = (health: `${AvcInstanceHealthEnum}`, mode?: Theme['palette']['mode']) => {
  switch (health) {
    case 'OK':
      return colors.green[variant(mode)]
    case 'WARNING':
      return colors.orange[variant(mode)]
    case 'CRITICAL':
      return colors.red[variant(mode)]
    case 'OFFLINE':
    default:
      return mode === ThemeTypes.DARK ? blackShades[500] : whiteShades[500]
  }
}

export const getAvcTaskStateColor = (
  state: `${AvcTaskStateEnum}` | `${AvcInstanceTaskStateEnum}`,
  mode?: Theme['palette']['mode'],
  health?: `${AvcTaskHealthEnum}`,
) => {
  switch (health) {
    case 'CRITICAL':
      return colors.red[variant(mode)]
    case 'WARNING':
      return colors.deepOrange[variant(mode)]
    default:
  }

  switch (state) {
    case 'STOPPING':
      return colors.deepOrange[variant(mode)]
    case 'PENDING':
    case 'STARTING':
    case 'PROVISIONING':
    case 'RESCHEDULED':
      return colors.blue[variant(mode)]
    case 'PROGRESSING':
    case 'COMPLETED':
      return colors.green[variant(mode)]
    case 'RETRYING':
      return colors.amber[variant(mode)]
    case 'BLOCKED':
    case 'FAILED':
      return colors.red[variant(mode)]
    case 'STOPPED':
    default:
      return mode === ThemeTypes.DARK ? blackShades[500] : whiteShades[500]
  }
}

export const getHealthColor = (health: HealthStates, mode?: Theme['palette']['mode']) => {
  switch (health) {
    case 'OK':
      return colors.green[variant(mode)]
    case 'WARNING':
      return colors.orange[variant(mode)]
    case 'CRITICAL':
      return colors.deepOrange[variant(mode)]
    case 'OFFLINE':
      return mode === ThemeTypes.DARK ? blackShades[500] : whiteShades[500]
    case 'UNKNOWN':
      return colors.blueGrey[variant(mode)]
  }
}

export const getStateColor = (state: EntityStates, mode?: Theme['palette']['mode'], health?: HealthStates) => {
  switch (health) {
    case 'CRITICAL':
      return colors.red[variant(mode)]
    case 'WARNING':
      return colors.deepOrange[variant(mode)]
    default:
  }

  switch (state) {
    case 'STOPPING':
      return colors.deepOrange[variant(mode)]
    case 'PENDING':
    case 'STARTING':
    case 'PROVISIONING':
    case 'RESCHEDULED':
    case 'Preparing':
      return colors.blue[variant(mode)]
    case 'PROGRESSING':
    case 'COMPLETED':
    case 'Live':
      return colors.green[variant(mode)]
    case 'Upcoming':
      return mode === ThemeTypes.DARK ? blackShades[900] : whiteShades[100]
    case 'RETRYING':
      return colors.amber[variant(mode)]
    case 'BLOCKED':
    case 'FAILED':
      return colors.red[variant(mode)]
    case 'STOPPED':
    case 'Ended':
    default:
      return mode === ThemeTypes.DARK ? blackShades[500] : whiteShades[500]
  }
}

export const getResourceStateColor = (state: ResourceStates) => {
  switch (state) {
    case 'ACTIVE':
      return colors.green
    case 'DELETED':
      return colors.red
    case 'ARCHIVED':
    default:
      return colors.grey
  }
}

export const getAlertSeverityColor = (state: AlertSeverities, mode?: Theme['palette']['mode']) => {
  switch (state) {
    case 'HIGH':
      return mode ? colors.red[variant(mode)] : colors.red
    case 'MEDIUM':
      return mode ? colors.orange[variant(mode)] : colors.orange
    case 'LOW':
    default:
      return mode ? colors.yellow[variant(mode)] : colors.yellow
  }
}

export const getLogEntryLevelColor = (state: LogEntryLevels, mode?: Theme['palette']['mode']) => {
  switch (state) {
    case 'Critical':
      return colors.red[variant(mode)]
    case 'Error':
      return colors.red[variant(mode)]
    case 'Warning':
      return colors.yellow[variant(mode)]
    case 'Info':
    default:
      return mode === ThemeTypes.DARK ? colors.grey[800] : colors.grey[200]
  }
}

export const getIsActiveColor = (state: boolean, mode?: Theme['palette']['mode']) => {
  switch (state) {
    case true:
      return colors.green[variant(mode)]
    case false:
      return colors.red[variant(mode)]
  }
}

export const getIsActiveStatusColor = (state: boolean, mode?: Theme['palette']['mode']) => {
  switch (state) {
    case true:
      return colors.green[variant(mode)]
    case false:
      return mode === ThemeTypes.DARK ? blackShades[500] : whiteShades[500]
  }
}
